import React, { useEffect } from "react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter } from "react-router-dom";
import { ScrollToTop } from "./ScrollToTop";
import { Router } from "@app/router/Router";
import { syncPendingDeliveries } from "@lib/offline/syncEngine";
import { useNetworkStatus } from "@lib/offline/networkDetector";
import { useAuthRefresh } from "@features/auth/hooks/useAuthRefresh";
import { NotificationStreamProvider } from "@components/notifications/NotificationStreamProvider";
import { I18nProvider } from "@lib/i18n/I18nProvider";
import { useThemeStore } from "@stores/theme";
import { initDevPerformanceLogging } from "@app/performance/devPerf";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            refetchOnWindowFocus: false,
            retry: 1,
            gcTime: 1000 * 60 * 60 * 24,
        },
    },
});

const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: "RIDER_QUERY_CACHE",
});

const SyncManager: React.FC = () => {
    const isOnline = useNetworkStatus();

    useEffect(() => {
        if (isOnline) {
            syncPendingDeliveries();
            const interval = setInterval(() => {
                syncPendingDeliveries();
            }, 30000);

            return () => clearInterval(interval);
        }
    }, [isOnline]);

    useEffect(() => {
        window.addEventListener("online", () => {
            syncPendingDeliveries();
        });
    }, []);

    return null;
};

export const App: React.FC = () => {
    useAuthRefresh();
    const theme = useThemeStore((s) => s.theme);

    useEffect(() => {
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(theme);
    }, [theme]);

    useEffect(() => {
        if (import.meta.env.DEV) initDevPerformanceLogging();
    }, []);

    return (
        <I18nProvider>
            <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
                <BrowserRouter>
                    <ScrollToTop />
                    <NotificationStreamProvider>
                        <Router />
                        <SyncManager />
                    </NotificationStreamProvider>
                </BrowserRouter>
            </PersistQueryClientProvider>
        </I18nProvider>
    );
};
